import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataTable, type DataTableColumn } from '@/components/common';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/stores/notificationStore';
import {
  useReviewShiftEndConfirmation,
  useShiftEndConfirmations,
  type ShiftEndConfirmation,
} from '@/hooks/useShiftEndConfirmation';

type ReviewFilter = 'needs-review' | 'all';

export function ShiftEndConfirmationsTab() {
  const { t, i18n } = useTranslation('hr');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const [filter, setFilter] = useState<ReviewFilter>('needs-review');
  const [reviewing, setReviewing] = useState<ShiftEndConfirmation | null>(null);
  const [notes, setNotes] = useState('');

  const { data, isLoading, isError, refetch } = useShiftEndConfirmations({
    needsReview: filter === 'needs-review',
  });
  const reviewMutation = useReviewShiftEndConfirmation();
  const rows = data ?? [];

  const columns: DataTableColumn<ShiftEndConfirmation>[] = useMemo(
    () => [
      {
        header: t('employee'),
        sortKey: 'employee',
        renderCell: (item) => (
          <Typography variant="body2">{item.employeeName ?? t('unknownEmployee')}</Typography>
        ),
      },
      {
        header: t('shiftEndConfirmations.scheduledEnd'),
        sortKey: 'scheduledShiftEnd',
        renderCell: (item) => (
          <Typography variant="body2">
            {new Date(item.scheduledShiftEnd).toLocaleString(i18n.language, {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </Typography>
        ),
      },
      {
        header: t('status'),
        sortKey: 'status',
        renderCell: (item) => (
          <Chip size="small" label={t(`shiftEndConfirmations.status.${item.status}`)} />
        ),
      },
      {
        header: t('shiftEndConfirmations.explanation'),
        renderCell: (item) => (
          <Typography variant="body2" color="text.secondary">
            {item.explanation ?? t('shiftEndConfirmations.noExplanation')}
          </Typography>
        ),
      },
      {
        header: t('shiftEndConfirmations.correctedEnd'),
        renderCell: (item) => (
          <Typography variant="body2">
            {item.correctedClockOutAt
              ? new Date(item.correctedClockOutAt).toLocaleString(i18n.language, {
                  dateStyle: 'short', timeStyle: 'short',
                })
              : '—'}
          </Typography>
        ),
      },
      {
        header: t('shiftEndConfirmations.review'),
        renderCell: (item) =>
          item.needsReview ? (
            <Button
              size="small"
              onClick={() => {
                setReviewing(item);
                setNotes('');
              }}
            >
              {t('shiftEndConfirmations.review')}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('shiftEndConfirmations.reviewed')}
            </Typography>
          ),
      },
    ],
    [i18n.language, t],
  );

  const sortCompareFn = (a: ShiftEndConfirmation, b: ShiftEndConfirmation, key: string): number => {
    switch (key) {
      case 'employee':
        return (a.employeeName ?? '').localeCompare(b.employeeName ?? '', 'fi');
      case 'scheduledShiftEnd':
        return new Date(a.scheduledShiftEnd).getTime() - new Date(b.scheduledShiftEnd).getTime();
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  };

  const handleReview = async () => {
    if (!reviewing) return;
    try {
      await reviewMutation.mutateAsync(
        notes.trim() === '' ? { id: reviewing.id } : { id: reviewing.id, notes },
      );
      notify.success(t('shiftEndConfirmations.reviewSaved'));
      setReviewing(null);
      setNotes('');
    } catch {
      notify.error(t('shiftEndConfirmations.reviewFailed'));
    }
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">{t('shiftEndConfirmations.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('shiftEndConfirmations.subtitle')}
          </Typography>
        </Box>

        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="shift-end-review-filter">{t('status')}</InputLabel>
          <Select
            labelId="shift-end-review-filter"
            label={t('status')}
            value={filter}
            onChange={(event) => setFilter(event.target.value as ReviewFilter)}
          >
            <MenuItem value="needs-review">{t('shiftEndConfirmations.needsReview')}</MenuItem>
            <MenuItem value="all">{t('allStatuses')}</MenuItem>
          </Select>
        </FormControl>

        {isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                {tc('errors.refresh')}
              </Button>
            }
          >
            {t('shiftEndConfirmations.loadFailed')}
          </Alert>
        )}

        <DataTable
          columns={columns}
          data={rows}
          getRowKey={(item) => item.id}
          loading={isLoading}
          emptyMessage={t('shiftEndConfirmations.empty')}
          sortCompareFn={sortCompareFn}
          defaultSortKey="scheduledShiftEnd"
          defaultSortDirection="desc"
          ariaLabel={t('shiftEndConfirmations.title')}
          renderMobileCard={(item) => (
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {item.employeeName ?? t('unknownEmployee')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(item.scheduledShiftEnd).toLocaleString(i18n.language, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip size="small" label={t(`shiftEndConfirmations.status.${item.status}`)} />
              </Box>
            </Box>
          )}
        />
      </Stack>

      <ResponsiveDialog
        open={Boolean(reviewing)}
        onClose={() => setReviewing(null)}
        fullWidth
        maxWidth="sm"
        mobileTitle={t('shiftEndConfirmations.review')}
      >
        <DialogTitle>{t('shiftEndConfirmations.review')}</DialogTitle>
        <DialogContent>
          {reviewing?.correctedClockOutAt && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('shiftEndConfirmations.correctedEnd')}: {new Date(reviewing.correctedClockOutAt).toLocaleString(i18n.language)}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            {reviewing?.explanation ?? t('shiftEndConfirmations.noExplanation')}
          </Typography>
          {reviewing?.status === 'pending' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('shiftEndConfirmations.pendingOpen')}
            </Typography>
          )}
          <TextField
            label={t('shiftEndConfirmations.reviewNotes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value.slice(0, 2000))}
            multiline
            minRows={3}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewing(null)}>{tc('cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleReview()}
            disabled={reviewMutation.isPending || reviewing?.status === 'pending'}
          >
            {t('shiftEndConfirmations.markReviewed')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
}
